import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import AppLoader from "components/AppLoader";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import appSettings from "appsettings.json";

function Cover() {
  const recaptchaSiteKey = appSettings?.security?.recaptchaSiteKey || "";
  const [phoneNo, setPhoneNo] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpStep, setOtpStep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [recaptchaOpen, setRecaptchaOpen] = useState(false);
  const [pendingOtpRequest, setPendingOtpRequest] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");

  useEffect(() => {
    if (!recaptchaOpen || !recaptchaSiteKey) return undefined;

    const existingScript = document.querySelector('script[src*="google.com/recaptcha/api.js"]');
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    const widgetId = "forgot-password-recaptcha";
    const renderCaptcha = () => {
      const container = document.getElementById(widgetId);
      const captchaApi = window.grecaptcha?.enterprise || window.grecaptcha;
      if (!container || !captchaApi || typeof captchaApi.render !== "function") return;
      if (container.getAttribute("data-rendered") === "true") return;

      captchaApi.render(widgetId, {
        sitekey: recaptchaSiteKey,
        callback: (token) => {
          setRecaptchaToken(token || "");
        },
        "expired-callback": () => {
          setRecaptchaToken("");
        },
      });
      container.setAttribute("data-rendered", "true");
    };

    const timer = window.setInterval(() => {
      const captchaApi = window.grecaptcha?.enterprise || window.grecaptcha;
      if (captchaApi && typeof captchaApi.render === "function") {
        renderCaptcha();
        window.clearInterval(timer);
      }
    }, 180);

    return () => window.clearInterval(timer);
  }, [recaptchaOpen, recaptchaSiteKey]);

  const requestOtp = async () => {
    if (!phoneNo.trim()) {
      setErrorMessage("Phone number is required.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    const requestOtpUrl = `${appSettings.baseUrl}${
      appSettings.endpoints.requestForgotPasswordOtp
    }?phoneNo=${encodeURIComponent(phoneNo)}`;

    try {
      const response = await fetch(requestOtpUrl, { method: "POST" });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to send OTP.");
      }

      setOtpStep(true);
      setSuccessMessage("OTP sent. Please check your registered phone number.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtpClick = async () => {
    if (!phoneNo.trim()) {
      setErrorMessage("Phone number is required.");
      return;
    }

    if (!recaptchaToken) {
      if (!recaptchaSiteKey) {
        setErrorMessage("reCAPTCHA site key is not configured in appsettings.json.");
        return;
      }
      setPendingOtpRequest(true);
      setRecaptchaOpen(true);
      return;
    }

    await requestOtp();
  };

  const handleRecaptchaContinue = async () => {
    if (!pendingOtpRequest || !recaptchaToken) return;
    setRecaptchaOpen(false);
    setPendingOtpRequest(false);
    await requestOtp();
  };

  const resetPassword = async () => {
    if (!phoneNo.trim() || !otp.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      setErrorMessage("Phone number, OTP and password fields are required.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("Password confirmation does not match.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    const resetPasswordUrl = `${appSettings.baseUrl}${
      appSettings.endpoints.resetForgotPasswordWithOtp
    }?phoneNo=${encodeURIComponent(phoneNo)}&otp=${encodeURIComponent(
      otp
    )}&newPassword=${encodeURIComponent(newPassword)}`;

    try {
      const response = await fetch(resetPasswordUrl, { method: "POST" });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Password reset failed.");
      }

      setSuccessMessage("Password reset successfully. You can now sign in.");
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
      setOtpStep(false);
    } catch (error) {
      setErrorMessage(error.message || "Unable to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasicLayout image="" wide>
      <Card
        sx={{
          width: "100%",
          maxWidth: 980,
          mx: "auto",
          borderRadius: 6,
          overflow: "hidden",
          boxShadow: "0 16px 30px rgba(2,6,23,0.18)",
        }}
      >
        <Grid container>
          <Grid item xs={12} md={4.5}>
            <MDBox
              sx={{
                bgcolor: "#55575b",
                color: "#ffffff",
                px: { xs: 3, md: 4 },
                py: { xs: 3, md: 4.5 },
                height: "100%",
                borderBottomRightRadius: { xs: 0, md: 64 },
              }}
            >
              <MDTypography variant="h2" fontWeight="bold" color="white">
                Forgot
              </MDTypography>
              <MDTypography variant="h2" fontWeight="bold" color="white">
                Password
              </MDTypography>
            </MDBox>
          </Grid>

          <Grid item xs={12} md={7.5}>
            <MDBox px={{ xs: 2.5, md: 5 }} py={{ xs: 2.8, md: 4 }}>
              <MDBox component="form" role="form">
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Phone Number"
                    value={phoneNo}
                    onChange={(e) => setPhoneNo(e.target.value)}
                    fullWidth
                  />
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    component={Link}
                    to="/authentication/sign-in"
                    variant="button"
                    color="primary"
                    fontWeight="medium"
                    textGradient
                  >
                    Back to Sign In
                  </MDTypography>
                </MDBox>

                {otpStep && (
                  <>
                    <MDBox mb={2}>
                      <MDInput
                        type="text"
                        label="OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        fullWidth
                      />
                    </MDBox>
                    <MDBox mt={-1} mb={2}>
                      <MDTypography variant="caption" color="warning" fontWeight="regular">
                        OTP is valid for 1 minute.
                      </MDTypography>
                    </MDBox>
                    <MDBox mb={2}>
                      <MDInput
                        type="password"
                        label="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        fullWidth
                      />
                    </MDBox>
                    <MDBox mb={2}>
                      <MDInput
                        type="password"
                        label="Confirm New Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        fullWidth
                      />
                    </MDBox>
                  </>
                )}

                {errorMessage && (
                  <MDBox mb={2}>
                    <MDTypography variant="button" color="error" fontWeight="regular">
                      {errorMessage}
                    </MDTypography>
                  </MDBox>
                )}

                {successMessage && (
                  <MDBox mb={2}>
                    <MDTypography variant="button" color="success" fontWeight="regular">
                      {successMessage}
                    </MDTypography>
                  </MDBox>
                )}

                <MDBox mt={3} mb={1} display="flex" justifyContent="center">
                  <MDButton
                    variant="gradient"
                    color="error"
                    sx={{ minWidth: 220, borderRadius: 4 }}
                    onClick={otpStep ? resetPassword : handleRequestOtpClick}
                    disabled={loading}
                  >
                    {loading ? <AppLoader size={20} /> : otpStep ? "Reset Password" : "Send OTP"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </Card>

      <Dialog open={recaptchaOpen} onClose={() => setRecaptchaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Security Verification</DialogTitle>
        <DialogContent>
          <MDTypography sx={{ mb: 2, color: "#475569" }}>
            Please complete the security check to continue.
          </MDTypography>
          {recaptchaSiteKey ? (
            <MDBox
              id="forgot-password-recaptcha"
              sx={{
                minHeight: 78,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
          ) : (
            <MDTypography sx={{ color: "#dc2626", fontSize: "0.9rem" }}>
              Missing reCAPTCHA site key in appsettings.json (security.recaptchaSiteKey).
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setRecaptchaOpen(false);
              setPendingOtpRequest(false);
            }}
            sx={{
              minWidth: 104,
              borderColor: "#d32f2f",
              color: "#b71c2a",
              fontWeight: 700,
              "&:hover": {
                borderColor: "#b71c2a",
                bgcolor: "#fff1f2",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!recaptchaToken}
            onClick={handleRecaptchaContinue}
            sx={{
              minWidth: 116,
              bgcolor: "#b71c2a",
              color: "#ffffff !important",
              fontWeight: 700,
              "&:hover": { bgcolor: "#991b1b" },
              "&.Mui-disabled": {
                bgcolor: "#d1d5db",
                color: "#6b7280 !important",
              },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </BasicLayout>
  );
}

export default Cover;
